import { styled, createTheme } from "@mui/material/styles";
import { Button, Switch, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import pillThemeConfig from "styles/pillThemeConfig";
import "@fontsource/urbanist";

export const theme = createTheme({
  typography: {
    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
    fontSize: 12, // Set a default font size
    letterSpacing: "0.10em", // Adjust the letter spacing as needed
    allVariants: {
      color: "#3F5765",
      letterSpacing: "0.10em", // Ensure letter spacing is applied to all variants
      fontSize: 12, // Ensure font size is applied to all variants
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          fontFamily: "Urbanist, Verdana, Arial, sans-serif",
          allVariants: {
            color: "#3F5765",
            letterSpacing: "0.10em", // Ensure letter spacing is applied to all text fields
            fontSize: 12, // Ensure font size is applied to all text fields
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          fontFamily: "Urbanist, Verdana, Arial, sans-serif",
          allVariants: {
            color: "#3F5765",
            letterSpacing: "0.10em", // Ensure letter spacing is applied to all data grid text
            fontSize: 12, // Ensure font size is applied to all data grid text
          },
        },
      },
    },
  },
});

export const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-root": {
    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
    fontSize: "12px", // Set the font size for the entire grid
  },
  "& .MuiDataGrid-columnsContainer": {
    backgroundColor: theme.palette.background.paper,
    fontSize: "12px", // Set the font size for the columns container
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #e0e0e0",
    fontSize: "12px", // Set the font size for the cells
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e0e0e0",
    fontSize: "12px", // Set the font size for the column headers
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: "bold",
    fontSize: "12px", // Set the font size for the column header titles
  },
  "& .MuiDataGrid-row": {
    backgroundColor: "#ffffff",
    marginTop: 0.5,
    marginBottom: 0.5,
    fontSize: "12px", // Set the font size for the rows
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  "& .coloured": {
    textAlign: "center",
    color: "#7181AD",
    fontSize: "12px", // Set the font size for custom colored cells
  },
  "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
    display: "none",
  },
}));

export const CustomButton = styled(Button)(({ theme }) => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  letterSpacing: "0.10em",
  fontSize: "12px", // Ensure font size is applied to buttons
}));

export const AntSwitch = styled(Switch, {
  shouldForwardProp: (prop) => prop !== "customBackgroundColor",
})(({ theme, customBackgroundColor }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor:
          customBackgroundColor ||
          (theme.palette.mode === "dark" ? "#177ddc" : "#3299FF"),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,.35)"
        : "rgba(0,0,0,.25)",
    boxSizing: "border-box",
  },
}));

export const useStyles = () => ({
  formControl: {
    minWidth: 120,
    transition: "background-color 0.6s ease-out, border-color 0.6s ease-out",
    "& .MuiOutlinedInput-notchedOutline": {
      transition: "border-color 0.6s ease-out",
    },
  },
  helperText: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "12px",
    fontWeight: "bold",
  },
  groupContainer: {
    borderRadius: "2px",
    border: "1px solid",
  },
  label: {
    fontFamily: "Urbanist, Arial, sans-serif",
    fontWeight: "bold",
    color: "#3F5765",
    paddingBottom: theme.spacing(0.5),
    letterSpacing: "0.10em", // Increase letter spacing for labels
    fontSize: "12px", // Ensure font size is applied to labels
    marginTop: theme.spacing(0.75), // Added bottom margin
    marginBottom: theme.spacing(0.75), // Added bottom margin
  },
  accordionSummary: {
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    },
    fontSize: "12px", // Ensure font size is applied to accordion summaries
  },
  accordionDetails: {
    fontSize: "12px", // Ensure font size is applied to accordion details
  },
  clearSectionButton: {
    position: "absolute",
    color: "#3F5765",
    border: "1px solid #3F5765",
    borderRadius: "4px",
    minHeight: "20px",
    minWidth: "20px",
  },
  lockButton: {
    color: "#2AA154",
    border: "1px solid #2AA154",
    padding: "2px",
    borderRadius: "4px",
    minHeight: "30px",
    minWidth: "30px",
    fontSize: "12px", // Ensure font size is applied to lock buttons
  },
  unLockButton: {
    color: "#EC6517",
    border: "1px solid #EC6517",
    padding: "2px",
    borderRadius: "4px",
    minHeight: "30px",
    minWidth: "30px",
    fontSize: "12px", // Ensure font size is applied to lock buttons
  },
  disabledLabel: {
    color: "#9e9e9e",
    fontSize: "12px", // Ensure font size is applied to disabled labels
  },
  disabledInput: {
    backgroundColor: "#e0e0e0",
    borderRadius: "4px",
    "& .MuiInputBase-root": {
      color: "#9e9e9e",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#bdbdbd",
    },
    "& .MuiSelect-icon": {
      color: "#9e9e9e",
    },
  },
  warningMessage01: {
    backgroundColor: "#ffe5e5",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    color: "black",
    fontSize: "12px", // Ensure font size is applied to warning messages
    marginBottom: theme.spacing(3), // Added bottom margin
  },
  warningMessage02: {
    backgroundColor: "#ffe5e5",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    color: "black",
    fontSize: "12px", // Ensure font size is applied to warning messages
    marginTop: theme.spacing(3), // Added bottom margin
  },
  warningMessage03: {
    backgroundColor: "#ffe5e5",
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    color: "black",
    fontSize: "12px", // Ensure font size is applied to warning messages
  },
  linkText: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: "12px", // Ensure font size is applied to link text
  },
  inputRoot: {
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
      {
        width: "2em", // Increase the size of the spin buttons
      },
    fontSize: "12px", // Ensure font size is applied to input root
  },
  inputSmall: {
    "& input": {
      padding: "8px 12px", // Adjust padding to reduce the size
      fontSize: "12px", // Ensure font size is applied to small input
    },
  },
  chipOG: {
    fontSize: "14px",
    fontWeight: "bold",
    letterSpacing: "1px",
    backgroundColor: pillThemeConfig.OG.primaryColor,
    color: pillThemeConfig.OG.fontColor,
  },

  chipLD: {
    fontSize: "14px",
    fontWeight: "bold",
    letterSpacing: "1px",
    backgroundColor: pillThemeConfig.LD.primaryColor,
    color: pillThemeConfig.LD.fontColor,
  },

  chipWFM: {
    fontSize: "14px",
    fontWeight: "bold",
    letterSpacing: "1px",
    backgroundColor: pillThemeConfig.WFM.primaryColor,
    color: pillThemeConfig.WFM.fontColor,
  },

  chipTA: {
    fontSize: "14px",
    fontWeight: "bold",
    letterSpacing: "1px",
    backgroundColor: pillThemeConfig.TA.primaryColor,
    color: pillThemeConfig.TA.fontColor,
  },

  chipOPS: {
    fontSize: "14px",
    fontWeight: "bold",
    letterSpacing: "1px",
    backgroundColor: pillThemeConfig.OPS.primaryColor,
    color: pillThemeConfig.OPS.fontColor,
  },

  typographyTime: {
    color: "#E23636",
    fontWeight: "bold",
  },

  maxLengthReached: {
    backgroundColor: "#fff8e1",
    borderRadius: "4px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#ffb300 !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#ffb300 !important",
    },
  },
});

export const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .MuiTooltip-tooltip`]: {
    fontSize: "0.9rem", // Adjust the font size as needed
  },
});
